import { z } from 'zod';
import { envelope, IsoDateTime, UserRef, Uuid } from './common.js';
import { defineRoute, IdParam } from './route.js';

export const SlackMilestone = z.enum([
  'REQUEST_RECEIVED',
  'REQUEST_ACCEPTED',
  'REQUEST_REJECTED',
  'REQUEST_RESOLVED',
  'REQUEST_NEEDS_ATTENTION',
  'WORK_CREATED',
  'WORK_STARTED',
  'WORK_IN_REVIEW',
  'WORK_BLOCKED',
  'WORK_INTEGRATED',
  'WORK_DEPLOYED',
  'WORK_DELIVERED',
  'WORK_CANCELED',
]);
export type SlackMilestone = z.infer<typeof SlackMilestone>;

export const SlackDestinationView = z.object({
  id: Uuid,
  scopeKind: z.enum(['PROJECT', 'TEAM']),
  scopeId: Uuid,
  workspaceId: z.string(),
  channelId: z.string(),
  approvedForRestricted: z.boolean(),
  milestones: z.array(z.string()),
  createdAt: IsoDateTime,
});
export type SlackDestinationView = z.infer<typeof SlackDestinationView>;

export const SetSlackDestination = z
  .object({
    scopeKind: z.enum(['PROJECT', 'TEAM']),
    scopeId: Uuid,
    workspaceId: z.string().regex(/^T[A-Z0-9]{2,20}$/),
    channelId: z.string().regex(/^[CG][A-Z0-9]{2,20}$/),
    approvedForRestricted: z.boolean(),
    milestones: z.array(SlackMilestone).min(1).max(13),
  })
  .strict();

export const SlackChannelView = z.object({ id: z.string(), name: z.string(), isPrivate: z.boolean(), isMember: z.boolean() });
export type SlackChannelView = z.infer<typeof SlackChannelView>;

/** The project's Slack channel as its managers see it in the project settings (ADR-0035). */
export const ProjectSlackView = z.object({
  /** Slack is connected in this environment. */
  connected: z.boolean(),
  canManage: z.boolean(),
  /** Channels the app can see; null when Slack is not connected, the caller cannot manage the project or the listing failed. */
  channels: z.array(SlackChannelView).nullable(),
  destinations: z.array(
    z.object({
      id: Uuid,
      channelId: z.string(),
      channelName: z.string().nullable(),
      approvedForRestricted: z.boolean(),
      milestones: z.array(SlackMilestone),
    }),
  ),
});
export type ProjectSlackView = z.infer<typeof ProjectSlackView>;

/** One channel for the project's updates, or none (channelId null). Replaces the project's destinations. */
export const SetProjectSlack = z
  .object({
    channelId: z.string().regex(/^[CG][A-Z0-9]{2,20}$/).nullable(),
    milestones: z.array(SlackMilestone).min(1).max(13),
  })
  .strict();
export type SetProjectSlack = z.infer<typeof SetProjectSlack>;

export const SlackLinkView = z.object({ workspaceId: z.string(), slackHandle: z.string() });

/** A Slack user ID, or the Enterprise Grid form "team:T0123:user:U0123". */
export const SlackUserParam = z.object({ slackUserId: z.string().regex(/^(team:[A-Z0-9]+:user:)?[UW][A-Z0-9]{2,20}$/) });

export const slackRoutes = {
  resolveSlackUser: defineRoute({ operationId: 'resolveSlackUser', method: 'GET', path: '/slack/users/:slackUserId', summary: 'The person behind a Slack member of the workspace (linked or provisioned from the confirmed company email)', tag: 'slack', auth: 'session', params: SlackUserParam, response: envelope(UserRef), successStatus: 200 }),
  getSlackLink: defineRoute({ operationId: 'getSlackLink', method: 'GET', path: '/slack/link', summary: 'Pending Slack account link (single-use state)', tag: 'slack', auth: 'session', query: z.object({ state: z.string().min(20).max(100) }), response: envelope(SlackLinkView), successStatus: 200 }),
  confirmSlackLink: defineRoute({ operationId: 'confirmSlackLink', method: 'POST', path: '/slack/link', summary: 'Explicitly link the Slack user to the signed-in account', tag: 'slack', auth: 'session', body: z.object({ state: z.string().min(20).max(100) }).strict(), response: envelope(z.object({ ok: z.literal(true) })), successStatus: 200 }),
  listSlackDestinations: defineRoute({ operationId: 'listSlackDestinations', method: 'GET', path: '/admin/slack/destinations', summary: 'Slack progress destinations', tag: 'slack', auth: 'session', response: envelope(z.array(SlackDestinationView)), successStatus: 200 }),
  setSlackDestination: defineRoute({ operationId: 'setSlackDestination', method: 'PUT', path: '/admin/slack/destinations', summary: 'Create or update an approved destination', tag: 'slack', auth: 'session', body: SetSlackDestination, response: envelope(z.array(SlackDestinationView)), successStatus: 200 }),
  deleteSlackDestination: defineRoute({ operationId: 'deleteSlackDestination', method: 'DELETE', path: '/admin/slack/destinations/:id', summary: 'Remove a destination', tag: 'slack', auth: 'session', params: IdParam, successStatus: 204 }),
  getProjectSlack: defineRoute({ operationId: 'getProjectSlack', method: 'GET', path: '/projects/:id/slack', summary: 'The Slack channel that receives the project updates, and the channels available', tag: 'slack', auth: 'session', params: IdParam, response: envelope(ProjectSlackView), successStatus: 200 }),
  setProjectSlack: defineRoute({ operationId: 'setProjectSlack', method: 'PUT', path: '/projects/:id/slack', summary: 'Choose the Slack channel for the project updates, or none (project managers and administrators)', tag: 'slack', auth: 'session', params: IdParam, body: SetProjectSlack, response: envelope(ProjectSlackView), successStatus: 200 }),
} as const;
